import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import InventoryItem from './InventoryItem';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import {
  IPhotoCard,
  IPhotoCardInventoryConfig,
  DEFAULT_INVENTORY_CONFIG,
} from '@typings/photocard/photocard.interface';
import InventoryDetailModal from './InventoryDetailModal';
import { CollectionPath, SettingCollectionPath } from '@typings/collections.enum';
import { ISoundEffect } from '@typings/setting/soundEffect.interface';
import { useAudioPlayer } from '@hooks/useAudioPlayer';
import { getPathKey } from '@utils/getPath';
import { IinventoryItem, IstoreItem } from '@typings/store/storeItem.interface';
import { scrollbarStyle } from '@styles/variants/scrollbar';
import ViewToggleTab, {
  ViewOption,
} from '@components/dataDisplay/tab/ViewToggleTab/ViewToggleTab';
import { ITEM_FUNCTION_TPYE } from '@typings/store/storeItemFunctionType';
import { masterSet } from '@masterSetting/masterSet/masterSetting';
import GridDroppable from '@components/library/DnD/GridDroppable';
import DragDropInfo from '@template/Info/DragDropInfo';
import InventoryGridView from './InventoryGridView';
import IconButton from '@components/dataEntry/button/IconButton';
import DropdownWrapper from '@components/wrapper/DropdownWrapper';
import { useControlPermission } from '@hooks/usePermission';

const ControlSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TabRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const TabsScroller = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;

  & > * {
    flex: 0 0 auto;
  }

  &::-webkit-scrollbar {
    display: none;
    height: 0;
  }
`;

const SortBtnWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const SortMenuItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  font-size: 12px;
  background: ${({ $active }) => ($active ? 'var(--inkColor)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--primaryColor)' : 'var(--textColor)')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover {
    background: var(--inkColor);
  }
`;

const ListWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: stretch;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  ${scrollbarStyle}
`;

const InventoryListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;


// 카테고리 타입 정의
type CategoryType =
  | 'all'
  | 'myroom'
  | 'gift'
  | 'skill_equipment'
  | 'pet'
  | 'investigation';

// 정렬 타입 정의
type SortType = 'custom' | 'latest' | 'alphabetical' | 'oldest';

// 확장된 인벤토리 아이템 타입 (스토어 정보 포함)
type EnhancedInventoryItem = IinventoryItem & {
  storeItemData?: IstoreItem;
  createdAt?: Date;
  title?: string;
};

const sortOptions: { key: SortType; value: string }[] = [
  { key: 'custom', value: '사용자 지정순' },
  { key: 'latest', value: '최신순' },
  { key: 'alphabetical', value: '가나다순' },
  { key: 'oldest', value: '오래된 순' },
];

type Props = {
  photocardData?: IPhotoCard;
  collectionPath?: string;
  photocardID?: string;
  defaultCategory?: CategoryType;
  showOnlyCategory?: CategoryType;
  listStyle?: React.CSSProperties;
  isTabEdit?: boolean;
  /** 관리자가 설정한 인벤토리 뷰 설정 (없으면 기본값 list 사용) */
  inventoryConfig?: IPhotoCardInventoryConfig;
};

function InventoryList({
  photocardData,
  collectionPath,
  photocardID,
  defaultCategory,
  showOnlyCategory,
  listStyle,
  isTabEdit,
  inventoryConfig,
}: Props) {
  // ─── 사운드 ──────────────────────────────────────────────────────────────────
  const { data: soundEffect } = useDocument<ISoundEffect>(
    SettingCollectionPath.SOUNDEFFECT,
    { listen: false },
  );
  const { play } = useAudioPlayer(
    { card: soundEffect?.cardSound?.url },
    Number(soundEffect?.volume) || 0.3,
  );
  const handleDndSound = () => {
    if (soundEffect?.isActive) play('card');
  };

  const resolvedConfig: IPhotoCardInventoryConfig =
    inventoryConfig ?? DEFAULT_INVENTORY_CONFIG;
  const isGridView = resolvedConfig.viewType === 'grid';
  const _collectionPath = collectionPath || getPathKey(1);
  const _photocardID = photocardID || getPathKey(2);

  // ─── 권한: owner 또는 master만 정렬/수정 아이콘 표시 ──────────
  const hasControlPermission = useControlPermission({
    authorUid: photocardData?.owner || photocardData?.authorUid || '',
  });

  const initialCategory = showOnlyCategory || defaultCategory || 'all';
  const [currentCategory, setCurrentCategory] =
    useState<CategoryType>(initialCategory);
  const [sortType, setSortType] = useState<SortType>('custom');
  const [enhancedItems, setEnhancedItems] = useState<EnhancedInventoryItem[]>(
    [],
  );

  const inventoryCollectionPath = `${_collectionPath}/${_photocardID}/inventory`;

  // 인벤토리 아이템 가져오기
  const { data: inventoryList } = useCollection<IinventoryItem>(
    inventoryCollectionPath,
    { listen: true, orderBy: ['createdAt', 'desc'] },
  );

  // 스토어 아이템 전체 가져오기
  const { data: storeItems } = useCollection<IstoreItem>(CollectionPath.STORE, {
    listen: false,
  });

  // 인벤토리 아이템과 스토어 아이템 매칭
  useEffect(() => {
    if (!inventoryList || !storeItems) {
      setEnhancedItems([]);
      return;
    }

    const storeItemMap = new Map<string, IstoreItem>();
    storeItems.forEach((item) => {
      if (item.id) storeItemMap.set(item.id, item);
    });

    const enhanced = inventoryList.map((inventoryItem) => ({
      ...inventoryItem,
      storeItemData: storeItemMap.get(inventoryItem.originItemID || ''),
    }));

    setEnhancedItems(enhanced);
  }, [inventoryList, storeItems]);

  // 카테고리별 필터링
  const filterByCategory = (
    items: EnhancedInventoryItem[],
    category: CategoryType,
  ) => {
    if (!items) return [];

    switch (category) {
      case 'all':
        return items;
      case 'myroom':
        return items.filter((item) => {
          const functionType =
            item.functionType || item.storeItemData?.functionType;
          return functionType === ITEM_FUNCTION_TPYE.MYROOM;
        });
      case 'gift':
        return items.filter(
          (item) => item.isPresent === true || !!item.giftingCharID,
        );
      case 'skill_equipment':
        return items.filter((item) => {
          const functionType =
            item.functionType || item.storeItemData?.functionType;
          return (
            functionType === ITEM_FUNCTION_TPYE.SKILL ||
            functionType === ITEM_FUNCTION_TPYE.EQUIPMENT
          );
        });
      case 'pet':
        return items.filter((item) => {
          const functionType =
            item.functionType || item.storeItemData?.functionType;
          return functionType === ITEM_FUNCTION_TPYE.PET;
        });
      case 'investigation':
        return items.filter((item) => {
          const functionType =
            item.functionType || item.storeItemData?.functionType;
          return functionType === ITEM_FUNCTION_TPYE.INVESTIGATION;
        });
      default:
        return items;
    }
  };

  // 정렬
  const sortItems = (items: EnhancedInventoryItem[], sort: SortType) => {
    if (!items) return [];
    const sortedItems = [...items];

    switch (sort) {
      case 'custom':
        // order 필드 기준 오름차순. 미설정 아이템은 맨 뒤
        return sortedItems.sort((a, b) => {
          const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
          // order가 동일하면 createdAt 오름차순으로 tie-break
          if (orderA === orderB) {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateA - dateB;
          }
          return orderA - orderB;
        });
      case 'latest':
        return sortedItems.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });
      case 'alphabetical':
        return sortedItems.sort((a, b) => {
          const titleA = a.storeItemData?.title || a.title || '';
          const titleB = b.storeItemData?.title || b.title || '';
          return titleA.localeCompare(titleB, 'ko');
        });
      case 'oldest':
        return sortedItems.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateA - dateB;
        });
      default:
        return sortedItems;
    }
  };

  const processedItems = useMemo(() => {
    const filtered = filterByCategory(enhancedItems, currentCategory);
    return sortItems(filtered, sortType);
  }, [enhancedItems, currentCategory, sortType]);

  const visibleItemIds = useMemo(() => {
    const filtered = filterByCategory(enhancedItems, currentCategory);
    return new Set(filtered.map((item) => item.id).filter(Boolean));
  }, [enhancedItems, currentCategory]);

  const getCategoryCount = (category: CategoryType) =>
    filterByCategory(enhancedItems, category).length;

  const allViewOptions: ViewOption[] = [
    {
      value: 'all',
      label: `전체 ${getCategoryCount('all')}`,
      isShow: true,
      drawView: null,
    },
    {
      value: 'myroom',
      label: `마이룸 ${getCategoryCount('myroom')}`,
      isShow: masterSet.stickerDoc || false,
      drawView: null,
    },
    {
      value: 'gift',
      label: `선물 ${getCategoryCount('gift')}`,
      isShow: true,
      drawView: null,
    },
    {
      value: 'investigation',
      label: `조사 ${getCategoryCount('investigation')}`,
      isShow: masterSet.explorationSystem || false,
      drawView: null,
    },
    {
      value: 'pet',
      label: `펫 ${getCategoryCount('pet')}`,
      isShow: masterSet.petSystem || false,
      drawView: null,
    },
  ];

  const viewOptions = showOnlyCategory
    ? allViewOptions.filter((option) => option.value === showOnlyCategory)
    : allViewOptions;

  // ─── 자체 편집 모드 (외부에서 isTabEdit을 제어하지 않을 때) ──────
  const [internalEdit, setInternalEdit] = useState(false);
  const isExternalEditControlled = isTabEdit !== undefined;
  const effectiveEdit = isExternalEditControlled ? isTabEdit : internalEdit;

  const handleToggleInternalEdit = useCallback(() => {
    setInternalEdit((prev) => !prev);
  }, []);

  // DnD 활성화: 편집모드 ON + 사용자 지정순일 때만
  const isDnDEnabled = !!effectiveEdit && sortType === 'custom';

  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortBtnRef = useRef<HTMLDivElement>(null);

  const handleSortClickOutside = useCallback((e: MouseEvent, dropdownNode: HTMLElement | null) => {
    if (!sortBtnRef.current || !dropdownNode) return;
    const isOutside =
      !sortBtnRef.current.contains(e.target as Node) &&
      !dropdownNode.contains(e.target as Node);
    if (isOutside) setIsSortOpen(false);
  }, []);

  const ITEM_SIZE = 50;
  const ITEM_GAP = 8;

  const listWrapperRef = useRef<HTMLDivElement>(null);
  const [dynamicLine, setDynamicLine] = useState(4);

  const rafRef = useRef<number>(0);
  const updateLine = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!listWrapperRef.current) return;
      const containerWidth = listWrapperRef.current.clientWidth;
      const computed = Math.max(1, Math.floor((containerWidth + ITEM_GAP) / (ITEM_SIZE + ITEM_GAP)));
      setDynamicLine(computed);
    });
  }, []);

  useEffect(() => {
    updateLine();
    const observer = new ResizeObserver(updateLine);
    if (listWrapperRef.current) observer.observe(listWrapperRef.current);
    return () => {
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
    };
  }, [updateLine]);

  const emptyMessage =
    currentCategory === 'all'
      ? '인벤토리가 비어있습니다.'
      : '해당 카테고리의 아이템이 없습니다.';

  return (
    <InventoryListContainer>
      <ControlSection>
          <TabRow>
            <TabsScroller>
              <ViewToggleTab
                viewOptions={viewOptions}
                defaultViewType={initialCategory}
                customSetViewType={setCurrentCategory}
                style={{ margin: 0 }}
                buttonStyle={{ fontSize: '12px', padding: '4px 8px', minWidth: 'unset' }}
              />
            </TabsScroller>

            {hasControlPermission && (
              <>
                {!isExternalEditControlled && (
                  <IconButton
                    icon="Edit18"
                    size="sm"
                    shape="default"
                    tooltip={effectiveEdit ? '수정 완료' : '수정'}
                    tooltipDir="bottom"
                    iconColor={
                      effectiveEdit ? 'var(--primaryColor)' : undefined
                    }
                    style={
                      effectiveEdit
                        ? { background: 'var(--inkColor)' }
                        : undefined
                    }
                    onClick={handleToggleInternalEdit}
                  />
                )}

                {!isGridView && (
                  <SortBtnWrapper ref={sortBtnRef}>
                    <IconButton
                      icon="Wheel18"
                      size="sm"
                      shape="default"
                      tooltip="정렬"
                      tooltipDir="bottom"
                      onClick={() => setIsSortOpen((v) => !v)}
                    />
                    <AnimatePresence>
                      {isSortOpen && (
                        <DropdownWrapper
                          direction="topRight"
                          open={isSortOpen}
                          handleClickOutside={handleSortClickOutside}
                        >
                          {sortOptions.map((opt) => (
                            <SortMenuItem
                              key={opt.key}
                              $active={sortType === opt.key}
                              onClick={() => {
                                setSortType(opt.key);
                                setIsSortOpen(false);
                              }}
                            >
                              {opt.value}
                            </SortMenuItem>
                          ))}
                        </DropdownWrapper>
                      )}
                    </AnimatePresence>
                  </SortBtnWrapper>
                )}
              </>
            )}
          </TabRow>
        </ControlSection>

      {isDnDEnabled && !isGridView && <DragDropInfo />}

      {isGridView ? (
        // ── 그리드 가방 뷰 ───────────────────────────────────────────────
        <InventoryGridView
          items={enhancedItems as IinventoryItem[]}
          config={resolvedConfig}
          inventoryCollectionPath={inventoryCollectionPath}
          isTabEdit={effectiveEdit}
          photocardData={photocardData}
          visibleItemIds={visibleItemIds}
        />
      ) : (
        // ── 리스트 뷰 ────────────────────────────────────────
        <ListWrapper ref={listWrapperRef} style={{ gap: ITEM_GAP, ...listStyle }}>
          {processedItems && processedItems.length > 0 ? (
            isDnDEnabled ? (
              // 편집모드 ON + 사용자 지정순: DnD 활성
              <GridDroppable
                collectionPath={inventoryCollectionPath}
                list={processedItems}
                line={dynamicLine}
                gap={`${ITEM_GAP}px`}
                minItemWidth={`${ITEM_SIZE}px`}
                photocardData={photocardData}
                modal={<InventoryDetailModal />}
                onDragStartCallback={handleDndSound}
                onDragEndCallback={handleDndSound}
              >
                <InventoryItem
                  collectionPath={CollectionPath.STORE}
                  followTooltip={false}
                />
              </GridDroppable>
            ) : (
              // 일반 모드 또는 다른 정렬: 기존 렌더링
              processedItems.map((item, i) => (
                <InventoryItem
                  key={`${item.id}-${i}`}
                  originItemID={item.originItemID || ''}
                  data={item}
                  photocardData={photocardData}
                  modal={<InventoryDetailModal />}
                  collectionPath={CollectionPath.STORE}
                />
              ))
            )
          ) : (
            <div
              style={{
                width: '100%',
                textAlign: 'center',
                color: 'var(--subTextColor)',
                padding: '40px 0',
                fontSize: '13px',
              }}
            >
              {emptyMessage}
            </div>
          )}
        </ListWrapper>
      )}
    </InventoryListContainer>
  );
}

export default InventoryList;
